import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { IDENTITY, METRICS } from "@/content";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
  { text: "Engineering", accent: false },
  { text: "Digital Growth", accent: true },
  { text: "through Data &", accent: false },
  { text: "Creative Precision", accent: false, italic: true },
];

const useISTClock = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: IDENTITY.timezone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const tick = () => setTime(fmt.format(new Date()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
};

export const Hero = () => {
  const time = useISTClock();
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: frameRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [-36, 36]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden px-6 pt-32 pb-20 sm:px-12 lg:px-20"
    >
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-terra/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[28rem] w-[28rem] rounded-full bg-honey/10 blur-[120px]" />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
            data-testid="hero-status"
          >
            <span className="flex items-center gap-1.5">
              <MapPin size={12} className="text-terra" />
              Pune, IN
            </span>
            <span className="text-border">|</span>
            <span data-testid="hero-clock">{time} IST</span>
            <span className="text-border">|</span>
            <span className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-sage" />
              Available for projects
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-terra" : ""} ${line.italic ? "italic text-muted-foreground" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.13, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg"
            data-testid="hero-subtitle"
          >
            {IDENTITY.name} — {IDENTITY.title}. I turn content, campaigns and
            analytics into measurable brand growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
            className="mt-10 flex flex-wrap gap-3"
            data-testid="hero-metrics"
          >
            {METRICS.map((m) => (
              <div
                key={m.label}
                className="rounded-full border border-border/70 bg-card/60 px-5 py-2.5 backdrop-blur-sm transition-colors duration-300 hover:border-terra/50"
              >
                <span className="font-serif text-lg text-terra">{m.value}</span>
                <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  {m.label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
        >
          <motion.div
            ref={frameRef}
            data-testid="hero-portrait-frame"
            onMouseMove={onTilt}
            onMouseLeave={() => {
              mx.set(0);
              my.set(0);
            }}
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            className="relative mx-auto w-full max-w-sm"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-terra/20 blur-3xl" aria-hidden="true" />
            <div className="img-texture relative overflow-hidden rounded-[2rem] border border-border/60 shadow-2xl">
              <motion.img
                src={IDENTITY.heroPhoto}
                alt={`Portrait of ${IDENTITY.name}`}
                style={{ y: parallaxY, scale: 1.12 }}
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-portrait-image"
              />
            </div>
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-border/60 bg-background/85 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground backdrop-blur-md">
              {IDENTITY.title}
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#about"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground transition-colors hover:text-terra"
      >
        <ChevronDown size={22} className="animate-pulse-soft" />
      </motion.a>
    </section>
  );
};
